import { useEffect, useMemo, useState } from "react";
import { api, API, uploadFile, formatApiErrorDetail } from "../lib/api";
import { CodeBlock } from "../components/CodeBlock";
import { Layout } from "../components/Layout";
import { toast } from "sonner";
import { UserCircleGear, Lightning, FloppyDisk, Archive, FileCode, Terminal, ListChecks, CaretDown, ArrowSquareOut, Eye, LockSimple, User, ClipboardText, UploadSimple, ArrowsClockwise, ArrowClockwise, Power } from "@phosphor-icons/react";

const UPLOAD_FIELDS = ["logo_url", "background_url", "logo_dark_url"];

export default function SSO() {
  const [providers, setProviders] = useState([]);
  const [meta, setMeta] = useState({ idps: [], modes: [], presets: {} });
  const [projects, setProjects] = useState([]);
  const [mode, setMode] = useState("xcreds");
  const [provider, setProvider] = useState("jamf");
  const [values, setValues] = useState({});
  const [result, setResult] = useState(null);
  const [tab, setTab] = useState("profile");
  const [building, setBuilding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [bundling, setBundling] = useState(false);
  const [saveTarget, setSaveTarget] = useState("new");
  const [title, setTitle] = useState("SSO configuration");
  const [guideOpen, setGuideOpen] = useState(true);

  useEffect(() => {
    api.get("/providers").then((r) => setProviders(r.data)).catch(() => {});
    api.get("/sso/meta").then((r) => setMeta(r.data)).catch(() => {});
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
  }, []);

  const modeSpec = useMemo(() => meta.modes.find((m) => m.id === mode), [meta, mode]);

  const labelFor = (key) => modeSpec?.params.find((p) => p.key === key)?.label || key;

  const guideSteps = useMemo(() => {
    const g = meta.guides?.[mode];
    if (!g) return [];
    const general = g._general || [];
    const idpSteps = modeSpec?.needs_idp ? g[values.idp] || [] : [];
    return [...general, ...idpSteps];
  }, [meta.guides, mode, modeSpec, values.idp]);

  useEffect(() => {
    if (!modeSpec) return;
    const init = {};
    modeSpec.params.forEach((p) => (init[p.key] = p.default));
    if (modeSpec.needs_idp && init.idp) {
      Object.assign(init, meta.presets?.[modeSpec.id]?.[init.idp] || {});
    }
    setValues(init);
    setResult(null);
    setTitle(`${modeSpec.name}`);
  }, [modeSpec, meta.presets]);

  const applyPreset = (idp) => {
    const preset = meta.presets?.[mode]?.[idp] || {};
    setValues((v) => ({ ...v, idp, ...preset }));
  };

  const setVal = (key, val) => {
    if (key === "idp") return applyPreset(val);
    setValues((v) => ({ ...v, [key]: val }));
  };

  const handleUpload = async (key, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const tid = toast.loading("Uploading…");
    try {
      const d = await uploadFile(file);
      setValues((v) => ({ ...v, [key]: d.url }));
      toast.success("Uploaded", { id: tid });
    } catch {
      toast.error("Upload failed", { id: tid });
    }
  };

  const copyValues = () => {
    const payload = { mode, provider, idp: modeSpec?.needs_idp ? values.idp : undefined, params: values };
    navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
    toast.success("Config values copied as JSON");
  };

  const importValues = () => {
    const raw = window.prompt("Paste a saved SSO config JSON:");
    if (!raw) return;
    try {
      const d = JSON.parse(raw);
      if (d.provider) setProvider(d.provider);
      const apply = () => setValues((v) => ({ ...v, ...(d.params || {}), ...(d.idp ? { idp: d.idp } : {}) }));
      if (d.mode && meta.modes.find((m) => m.id === d.mode) && d.mode !== mode) {
        setMode(d.mode);
        setTimeout(apply, 60);
      } else {
        apply();
      }
      toast.success("Config imported");
    } catch { toast.error("Invalid JSON"); }
  };

  const idpName = { entra: "Microsoft", okta: "Okta", google: "Google" };

  const brandButton = () => {
    const idp = values.idp;
    if (idp === "google")
      return (
        <div className="w-full bg-white text-[#3c4043] text-xs font-medium py-2 flex items-center justify-center gap-2">
          <span className="text-sm font-bold leading-none"><span className="text-[#4285F4]">G</span></span>
          Sign in with Google
        </div>
      );
    if (idp === "okta")
      return (
        <div className="w-full bg-[#00297A] text-white text-xs font-medium py-2 flex items-center justify-center gap-2">
          <span className="w-3.5 h-3.5 rounded-full border-2 border-white inline-block" />
          Sign in with Okta
        </div>
      );
    return (
      <div className="w-full bg-white text-[#5e5e5e] text-xs font-medium py-2 flex items-center justify-center gap-2">
        <span className="grid grid-cols-2 gap-px w-3.5 h-3.5">
          <span className="bg-[#f25022]" /><span className="bg-[#7fba00]" />
          <span className="bg-[#00a4ef]" /><span className="bg-[#ffb900]" />
        </span>
        Sign in with Microsoft
      </div>
    );
  };

  const build = async () => {
    setBuilding(true);
    setResult(null);
    try {
      const { data } = await api.post("/sso/build", { mode, provider, params: values });
      setResult(data);
      setTab("profile");
      toast.success("Generated — 0 tokens used");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Build failed");
    } finally {
      setBuilding(false);
    }
  };

  const activeArtifact = () => (tab === "profile" ? result.profile : result.script);

  const save = async () => {
    if (!result) return;
    setSaving(true);
    const art = activeArtifact();
    try {
      if (saveTarget === "new") {
        const { data } = await api.post("/projects", {
          title: `${title} (${tab})`,
          provider: result.provider,
          output_type: tab === "profile" ? "profile" : "script",
          prompt: `SSO: ${mode}`,
          content: art.content,
          language: art.language,
          filename: art.filename,
          deploy_notes: result.deploy_notes,
        });
        setProjects((p) => [data, ...p]);
        toast.success(`Saved ${tab} as new project`);
      } else {
        const { data } = await api.post(`/projects/${saveTarget}/versions`, {
          content: art.content,
          language: art.language,
          filename: art.filename,
          deploy_notes: result.deploy_notes,
          prompt: `SSO: ${mode}`,
          note: `SSO ${mode} (${tab})`,
        });
        setProjects((list) => list.map((p) => (p.id === data.id ? data : p)));
        toast.success(`Saved new version to "${data.title}"`);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const downloadBundle = async () => {
    if (!result) return;
    setBundling(true);
    try {
      const resp = await fetch(`${API}/bundle-multi`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          title: title || "SSO configuration",
          provider: result.provider,
          deploy_notes: result.deploy_notes,
          files: [
            { filename: result.profile.filename, content: result.profile.content },
            { filename: result.script.filename, content: result.script.content },
          ],
        }),
      });
      if (!resp.ok) throw new Error();
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${result.provider}-${mode}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Bundle downloaded (profile + script)");
    } catch {
      toast.error("Bundle download failed");
    } finally {
      setBundling(false);
    }
  };

  const inputCls =
    "w-full bg-[#050505] border border-[#27272A] px-3 py-2 text-sm font-mono focus:border-[#00F0FF] focus:outline-none transition-colors duration-200";
  const labelCls = "block font-mono text-[10px] text-[#71717A] mb-1.5 uppercase tracking-wider";

  return (
    <Layout>
      <div className="border-b border-[#27272A] px-8 py-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl tracking-tight flex items-center gap-2">
            <UserCircleGear size={22} className="text-[#00F0FF]" /> SSO / Login
          </h1>
          <p className="text-sm text-[#A1A1AA] font-mono mt-1">
            Build login-window SSO &amp; identity configs — profile + deploy script.
          </p>
        </div>
        <span className="hidden md:flex items-center gap-2 font-mono text-xs text-[#00FF41] border border-[#00FF41]/40 bg-[#00FF41]/5 px-3 py-1.5">
          <Lightning size={13} weight="fill" /> 0 LLM TOKENS
        </span>
      </div>

      <div className="p-8 space-y-8 max-w-5xl">
        {/* Mode tabs */}
        <div className="flex flex-wrap gap-2" data-testid="sso-modes">
          {meta.modes.map((m) => (
            <button
              key={m.id}
              data-testid={`sso-mode-${m.id}`}
              onClick={() => setMode(m.id)}
              className={`px-4 py-2 border text-sm transition-colors duration-200 ${
                mode === m.id ? "border-[#00F0FF] text-[#00F0FF] bg-[#00F0FF]/5" : "border-[#27272A] text-[#A1A1AA] hover:border-[#3f3f46]"
              }`}
            >
              {m.name}
            </button>
          ))}
        </div>

        {modeSpec && <p className="text-sm text-[#A1A1AA] font-mono">{modeSpec.desc}</p>}

        {/* Setup guide */}
        {guideSteps.length > 0 && (
          <div className="border border-[#27272A] bg-[#121214]" data-testid="sso-guide">
            <button
              data-testid="sso-guide-toggle"
              onClick={() => setGuideOpen((o) => !o)}
              className="w-full flex items-center justify-between px-5 py-3.5 text-left"
            >
              <span className="flex items-center gap-2 text-sm">
                <ListChecks size={18} className="text-[#00F0FF]" />
                Setup guide — grab these pieces {modeSpec?.needs_idp ? `(${values.idp})` : ""}
              </span>
              <CaretDown size={16} className={`text-[#71717A] transition-transform duration-200 ${guideOpen ? "rotate-180" : ""}`} />
            </button>
            {guideOpen && (
              <ol className="border-t border-[#27272A] divide-y divide-[#27272A]">
                {guideSteps.map((s, i) => (
                  <li key={i} className="flex gap-4 px-5 py-4">
                    <span className="font-mono text-xs text-[#00F0FF] w-6 shrink-0 pt-0.5">{String(i + 1).padStart(2, "0")}</span>
                    <div className="min-w-0">
                      <div className="text-sm text-[#FAFAFA]">{s.title}</div>
                      <p className="text-xs text-[#A1A1AA] font-mono leading-relaxed mt-1">{s.detail}</p>
                      <div className="flex flex-wrap items-center gap-3 mt-2">
                        {s.field && (
                          <span className="font-mono text-[10px] text-[#00FF41] border border-[#00FF41]/30 bg-[#00FF41]/5 px-2 py-0.5">
                            fills: {labelFor(s.field)}
                          </span>
                        )}
                        {s.link && (
                          <a href={s.link} target="_blank" rel="noreferrer" className="flex items-center gap-1 font-mono text-[10px] text-[#00F0FF] hover:underline">
                            <ArrowSquareOut size={12} /> Open docs
                          </a>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </div>
        )}

        {/* Provider */}
        <div>
          <label className={labelCls}>MDM Provider</label>
          <div className="flex flex-wrap gap-2">
            {providers.map((p) => (
              <button
                key={p.id}
                data-testid={`sso-provider-${p.id}`}
                onClick={() => setProvider(p.id)}
                className={`flex items-center gap-2 px-3 py-2 border text-xs transition-colors duration-200 ${
                  provider === p.id ? "border-[#00F0FF] text-[#00F0FF]" : "border-[#27272A] text-[#A1A1AA] hover:border-[#3f3f46]"
                }`}
              >
                <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />
                {p.name}
              </button>
            ))}
          </div>
        </div>

        {/* Params */}
        {modeSpec && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="sso-params">
            {modeSpec.params.map((p) => (
              <div key={p.key} className={p.type === "bool" ? "md:col-span-2" : ""}>
                {p.type !== "bool" && <label className={labelCls}>{p.label}</label>}
                {p.type === "bool" ? (
                  <button
                    data-testid={`sso-param-${p.key}`}
                    onClick={() => setVal(p.key, !values[p.key])}
                    className="flex items-center gap-2 text-sm text-[#A1A1AA]"
                  >
                    <span className={`w-10 h-5 border flex items-center px-0.5 transition-colors duration-200 ${values[p.key] ? "border-[#00F0FF] bg-[#00F0FF]/10 justify-end" : "border-[#27272A] justify-start"}`}>
                      <span className={`w-3.5 h-3.5 ${values[p.key] ? "bg-[#00F0FF]" : "bg-[#52525b]"}`} />
                    </span>
                    {p.label}
                  </button>
                ) : p.type === "select" ? (
                  <select data-testid={`sso-param-${p.key}`} value={values[p.key] ?? ""} onChange={(e) => setVal(p.key, e.target.value)} className={inputCls}>
                    {p.options.map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </select>
                ) : UPLOAD_FIELDS.includes(p.key) ? (
                  <div className="flex gap-2">
                    <input
                      data-testid={`sso-param-${p.key}`}
                      value={values[p.key] ?? ""}
                      onChange={(e) => setVal(p.key, e.target.value)}
                      className={inputCls}
                      placeholder="paste URL or upload"
                    />
                    <label className="flex items-center gap-1.5 border border-[#27272A] px-3 text-xs text-[#A1A1AA] hover:border-[#00F0FF] hover:text-[#00F0FF] cursor-pointer transition-colors duration-200 whitespace-nowrap">
                      <UploadSimple size={14} /> Upload
                      <input type="file" accept="image/*" className="hidden" data-testid={`upload-${p.key}`} onChange={(e) => handleUpload(p.key, e)} />
                    </label>
                  </div>
                ) : (
                  <input
                    data-testid={`sso-param-${p.key}`}
                    type={p.type === "password" ? "password" : "text"}
                    value={values[p.key] ?? ""}
                    onChange={(e) => setVal(p.key, e.target.value)}
                    className={inputCls}
                  />
                )}
              </div>
            ))}
          </div>
        )}

        {mode === "xcreds" && (
          <div data-testid="xcreds-preview">
            <label className={labelCls + " flex items-center gap-1.5"}><Eye size={12} /> Live login-screen preview</label>
            <div
              className="relative w-full aspect-[16/9] max-w-2xl border border-[#27272A] overflow-hidden flex flex-col items-center justify-center"
              style={{
                background: values.background_url
                  ? `#000 center/cover no-repeat url("${values.background_url}")`
                  : "linear-gradient(135deg,#0b1220,#111827 60%,#0a0a0b)",
              }}
            >
              <div className="absolute inset-0 bg-black/45" />
              <div className="relative z-[2] flex flex-col items-center px-6 text-center w-full max-w-xs">
                {values.logo_url ? (
                  <img src={values.logo_url} alt="logo" className="h-14 object-contain mb-4" onError={(e) => (e.currentTarget.style.display = "none")} />
                ) : (
                  <div className="w-14 h-14 rounded-full bg-white/10 border border-white/20 flex items-center justify-center mb-4">
                    <User size={26} className="text-white/70" />
                  </div>
                )}
                {values.welcome_text && (
                  <div className="text-white text-sm font-medium mb-4 drop-shadow">{values.welcome_text}</div>
                )}
                <div className="w-full">{brandButton()}</div>
                {values.help_text && (
                  <div className="mt-3 text-[10px] font-mono text-white/50">{values.help_text}</div>
                )}
              </div>
              {values.show_power_buttons && (
                <div className="absolute bottom-3 left-4 z-[2] flex items-end gap-4 text-white/75">
                  <div className="flex flex-col items-center gap-0.5"><ArrowsClockwise size={16} /><span className="text-[8px] font-mono">Refresh</span></div>
                  <div className="flex flex-col items-center gap-0.5"><ArrowClockwise size={16} /><span className="text-[8px] font-mono">Restart</span></div>
                  <div className="flex flex-col items-center gap-0.5"><Power size={16} /><span className="text-[8px] font-mono">Shut Down</span></div>
                </div>
              )}
              {values.show_local_first && (
                <div className="absolute bottom-3 right-4 z-[2] text-[10px] font-mono text-white/85 border border-white/25 px-2.5 py-1 bg-white/10 backdrop-blur-sm">
                  Local Sign In
                </div>
              )}
              <div className="absolute top-2 right-3 z-[2] font-mono text-[9px] text-white/40">XCreds · preview</div>
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-3">
          <button
            data-testid="build-sso-btn"
            onClick={build}
            disabled={building}
            className="flex items-center gap-2 bg-[#00F0FF] text-[#0A0A0B] font-medium px-6 py-3 text-sm hover:bg-[#00d4e0] transition-colors duration-200 disabled:opacity-50"
          >
            <Lightning size={16} weight="fill" /> {building ? "Building…" : "Build config (0 tokens)"}
          </button>
          <button
            data-testid="import-json-btn"
            onClick={importValues}
            className="flex items-center gap-2 border border-[#27272A] text-[#A1A1AA] px-5 py-3 text-sm hover:border-[#00F0FF] hover:text-[#00F0FF] transition-colors duration-200"
          >
            <ClipboardText size={16} /> Import config
          </button>
          <button
            data-testid="copy-json-btn"
            onClick={copyValues}
            className="flex items-center gap-2 border border-[#27272A] text-[#A1A1AA] px-5 py-3 text-sm hover:border-[#00F0FF] hover:text-[#00F0FF] transition-colors duration-200"
          >
            <ClipboardText size={16} /> Copy values as JSON
          </button>
        </div>

        {result && (
          <div className="space-y-4" data-testid="sso-output">
            {/* Profile / Script tabs */}
            <div className="flex border border-[#27272A] w-fit">
              <button
                data-testid="sso-tab-profile"
                onClick={() => setTab("profile")}
                className={`flex items-center gap-2 px-4 py-2 text-sm transition-colors duration-200 ${tab === "profile" ? "bg-[#00F0FF]/10 text-[#00F0FF]" : "text-[#A1A1AA] hover:text-[#FAFAFA]"}`}
              >
                <FileCode size={15} /> Profile
              </button>
              <button
                data-testid="sso-tab-script"
                onClick={() => setTab("script")}
                className={`flex items-center gap-2 px-4 py-2 text-sm border-l border-[#27272A] transition-colors duration-200 ${tab === "script" ? "bg-[#00F0FF]/10 text-[#00F0FF]" : "text-[#A1A1AA] hover:text-[#FAFAFA]"}`}
              >
                <Terminal size={15} /> Deploy Script
              </button>
            </div>

            <div className="flex flex-col md:flex-row gap-3">
              <select
                data-testid="sso-save-target"
                value={saveTarget}
                onChange={(e) => setSaveTarget(e.target.value)}
                className="flex-1 bg-[#050505] border border-[#27272A] px-3 py-2 text-sm font-mono focus:border-[#00F0FF] focus:outline-none transition-colors duration-200"
              >
                <option value="new">＋ New project</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>↳ Add version to: {p.title}</option>
                ))}
              </select>
              <div className="flex gap-3">
                <button data-testid="sso-save-btn" onClick={save} disabled={saving} className="flex items-center gap-2 border border-[#00FF41] text-[#00FF41] px-4 py-2 text-sm hover:bg-[#00FF41]/10 transition-colors duration-200 disabled:opacity-50">
                  <FloppyDisk size={16} /> {saving ? "…" : `Save ${tab}`}
                </button>
                <button data-testid="sso-bundle-btn" onClick={downloadBundle} disabled={bundling} className="flex items-center gap-2 border border-[#0A84FF] text-[#0A84FF] px-4 py-2 text-sm hover:bg-[#0A84FF]/10 transition-colors duration-200 disabled:opacity-50">
                  <Archive size={16} /> {bundling ? "…" : "Bundle both"}
                </button>
              </div>
            </div>

            <CodeBlock
              code={tab === "profile" ? result.profile.content : result.script.content}
              language={tab === "profile" ? result.profile.language : result.script.language}
              filename={tab === "profile" ? result.profile.filename : result.script.filename}
            />

            {result.deploy_notes && (
              <div className="border border-[#27272A] bg-[#121214] p-4">
                <div className="font-mono text-xs text-[#00F0FF] mb-2">DEPLOYMENT · {result.provider.toUpperCase()}</div>
                <p className="text-sm text-[#A1A1AA] leading-relaxed">{result.deploy_notes}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
